import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  agentCommandsQueryOptions,
  commandsQueryOptions,
  expandCommand,
  type Command,
} from "../queries/commands-queries";
import { agentsCatalogueQueryOptions } from "../queries/agents-queries";
import { transcribeAudio } from "../api/transcribe";
import { HeroAgentPill } from "./HeroAgentPill";
import { HeroConnectorsPill } from "./HeroConnectorsPill";
import { MentionPopover } from "./MentionPopover";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import { prehashFolder } from "../hooks/use-doc-sync";
import { uploadAttachmentsToSession } from "../lib/attachments";

// Pasted text larger than this (UTF-8 bytes) is diverted into a .txt attachment
// instead of going inline. A multi-MB log pasted inline blows past the model's
// context window on the very first turn (and is unreadable in the bubble); as a
// file the agent can read it in chunks via read_file. ~50 KB ≈ a long error
// dump, well below anything that threatens the window.
const PASTE_TO_FILE_BYTES = 50_000;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

interface Props {
  onSend: (text: string, command?: { label: string }) => void;
  onStop: () => void;
  disabled: boolean;
  isStreaming: boolean;
  sessionId: string | null;
  folderConnected: boolean;
  folderName: string | null;
  folderPickerSupported: boolean;
  onPickFolder: () => Promise<void>;
  /** Network-folder (WebDAV) source is available — show a Local/Network
   *  chooser on the folder button instead of opening the local picker
   *  directly. Off on stands without the feature flag. */
  networkFolderEnabled?: boolean;
  /** Open the network-folder browser. */
  onPickNetworkFolder?: () => void;
  onDisconnectFolder: () => void;
  heroMode: boolean;
  syncStatus: {
    active: boolean;
    direction: "upload" | "download" | "";
    current: number;
    total: number;
    warning: string;
  };
  /**
   * Ref of the agent active in this chat (`system:<id>` / `user:<uuid>`).
   * When provided, the slash menu resolves commands server-side via
   * `/api/agents/{ref}/commands` — that surfaces commands attached to a
   * shared agent even if the caller doesn't personally own them
   * (the "gateway through agent" sharing model). Parents resolve this
   * from the active session's snapshot (in chat) or omit it for the
   * agent-builder test pane (which uses `commandRefs` instead).
   */
  agentRef?: string | null;
  /**
   * Refs to filter the caller's *own* commands against. Used only by
   * the agent-builder test pane, which has no saved agent yet — it just
   * intersects personal commands with the unsaved form's `command_refs`.
   * Main chat passes `agentRef` instead.
   */
  commandRefs?: string[] | null;
  /** Hide the folder/connectors footer — test chat in agent builder uses this. */
  hideFooter?: boolean;
}

/** Staged "+"-attachment chips: a single non-wrapping row. Chips that don't
 *  fit collapse into a trailing "+N" badge. Widths are measured off an
 *  invisible full copy so the visible slice can't feed back into the
 *  measurement (which would oscillate). */
function HeroFileChips({ files, onRemove }: { files: File[]; onRemove: (i: number) => void }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(files.length);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;

    const GAP = 6;
    const BADGE_RESERVE = 52; // room kept for the "+N" badge when chips spill

    const recompute = () => {
      const avail = row.clientWidth;
      if (avail === 0) return;
      const widths = Array.from(measure.children).map((c) => (c as HTMLElement).offsetWidth);
      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i++) {
        used += widths[i]! + (i > 0 ? GAP : 0);
        const reserve = i === widths.length - 1 ? 0 : BADGE_RESERVE;
        if (used + reserve > avail) break;
        count++;
      }
      setVisible(Math.max(1, count));
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(row);
    return () => ro.disconnect();
  }, [files]);

  const hidden = files.length - visible;

  return (
    <div className="hero-file-row" ref={rowRef}>
      <div className="hero-file-measure" aria-hidden="true" ref={measureRef}>
        {files.map((f, i) => (
          <span key={i} className="hero-file-chip">
            <span className="hero-file-chip-name">{f.name}</span>
            <span className="hero-file-chip-x">×</span>
          </span>
        ))}
      </div>
      {files.slice(0, visible).map((f, i) => (
        <span key={`${f.name}-${i}`} className="hero-file-chip" title={f.name}>
          <span className="hero-file-chip-name">{f.name}</span>
          <button
            type="button"
            className="hero-file-chip-x"
            onClick={() => onRemove(i)}
            aria-label={`Убрать ${f.name}`}
            title="Убрать файл"
          >
            ×
          </button>
        </span>
      ))}
      {hidden > 0 && (
        <span
          className="hero-file-chip hero-file-chip--more"
          title={files.slice(visible).map((f) => f.name).join("\n")}
        >
          +{hidden}
        </span>
      )}
    </div>
  );
}

function parseCommand(text: string): [string, string] | null {
  const m = text.match(/^\/(\S+)(?:\s+([\s\S]*))?$/);
  if (!m || !m[1]) return null;
  return [m[1], m[2] ?? ""];
}

function expandTemplateWithArgs(template: string, args: string): string {
  // If the template references $ARGUMENTS we substitute; otherwise we glue
  // any user-typed args onto the end of the template separated by a blank
  // line, so the chip is still useful for "no-arg" commands like /summarize.
  if (template.includes("$ARGUMENTS")) {
    return template.replace(/\$ARGUMENTS/g, args).trim();
  }
  const trimmed = args.trim();
  return trimmed ? `${template.trim()}\n\n${trimmed}` : template.trim();
}

export function InputBar({
  onSend,
  onStop,
  disabled,
  isStreaming,
  sessionId,
  folderConnected,
  folderName,
  folderPickerSupported,
  onPickFolder,
  networkFolderEnabled = false,
  onPickNetworkFolder,
  onDisconnectFolder,
  heroMode,
  syncStatus,
  agentRef,
  commandRefs,
  hideFooter = false,
}: Props) {
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [menuIndex, setMenuIndex] = useState(0);
  const [agentMenuIndex, setAgentMenuIndex] = useState(0);
  const [mentionDismissedAt, setMentionDismissedAt] = useState<number | null>(null);
  // True when the @-button (or pill click) just inserted "@" while a slash
  // command chip is set. Bypasses the chip-block in mentionToken so the menu
  // opens — the user explicitly asked to switch agent. Auto-clears when the
  // mention token is gone (user dismissed or picked).
  const [mentionForceOpen, setMentionForceOpen] = useState(false);
  // True when the @-button was clicked but there are no agents to pick (only
  // the implicit default). Opens the drawer's empty state instead of inserting
  // a stray "@". Independent of any mention token — there's nothing to type.
  const [agentHelpOpen, setAgentHelpOpen] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const [recordingState, setRecordingState] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const pendingAgentRefForPill = useAgentDraftStore((s) => s.pendingRef);
  const setPendingAgentRef = useAgentDraftStore((s) => s.setPendingRef);
  const setPendingFolderHandle = useAgentDraftStore((s) => s.setPendingFolderHandle);
  const pendingFiles = useAgentDraftStore((s) => s.pendingFiles);
  const setPendingFiles = useAgentDraftStore((s) => s.setPendingFiles);
  const heroSyncing = useAgentDraftStore((s) => s.heroSync?.status === "uploading");

  const { data: personalCommands } = useQuery(commandsQueryOptions());
  const { data: agentsData } = useQuery(agentsCatalogueQueryOptions());
  const allAgents = agentsData?.agents ?? [];
  const defaultAgent = agentsData?.defaultAgent ?? null;

  // In hero mode the active agent comes from the draft store (pill / @-pick).
  // In session mode it comes from the `agentRef` prop. The test pane in
  // AgentBuilder is the only path that uses `commandRefs` (no saved agent yet).
  const heroActiveAgent = useMemo(() => {
    if (!heroMode) return null;
    if (pendingAgentRefForPill) {
      return allAgents.find((a) => a.ref === pendingAgentRefForPill) ?? null;
    }
    return defaultAgent;
  }, [heroMode, pendingAgentRefForPill, allAgents, defaultAgent]);

  const effectiveAgentRef = useMemo<string | null>(() => {
    if (heroMode) return heroActiveAgent?.ref ?? null;
    return agentRef ?? null;
  }, [heroMode, heroActiveAgent, agentRef]);

  // Live resolution via the agent — picks up foreign-owned commands attached
  // by other space members.
  const { data: agentResolvedCommands } = useQuery(
    agentCommandsQueryOptions(effectiveAgentRef),
  );

  const commands = useMemo<Command[] | undefined>(() => {
    if (effectiveAgentRef) return agentResolvedCommands;
    // Fallback path — test pane in AgentBuilder: intersect personal commands
    // with the unsaved form's `command_refs`.
    if (!personalCommands) return undefined;
    if (commandRefs == null) return personalCommands;
    const allow = new Set(commandRefs);
    return personalCommands.filter((c) => allow.has(c.ref));
  }, [effectiveAgentRef, agentResolvedCommands, personalCommands, commandRefs]);
  const commandsById = useMemo(() => {
    const m = new Map<string, Command>();
    (commands ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [commands]);

  // Suppress the popup once a command is selected as a chip — the textarea
  // is now for arguments, not a new command.
  const showMenu = !selectedCommand && /^\/[^\s]*$/.test(text);
  const query = showMenu ? text.slice(1).toLowerCase() : "";
  const suggestions = useMemo(() => {
    if (!showMenu || !commands) return [];
    return commands.filter(
      (c) =>
        c.label.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.id.toLowerCase().includes(query),
    );
  }, [showMenu, commands, query]);

  const menuOpen = showMenu && suggestions.length > 0;
  // Keep the last non-empty list around during close so the collapse
  // animation has content to shrink past instead of vanishing.
  const lastSuggestionsRef = useRef<Command[]>([]);
  if (suggestions.length > 0) lastSuggestionsRef.current = suggestions;
  const renderedSuggestions = menuOpen ? suggestions : lastSuggestionsRef.current;

  useEffect(() => { setMenuIndex(0); }, [query]);

  const sortedAgents = useMemo(() => {
    const pool = defaultAgent
      ? allAgents.filter((a) => a.ref !== defaultAgent.ref)
      : allAgents;
    return [...pool].sort((a, b) => {
      const fav = Number(b.is_favorite) - Number(a.is_favorite);
      if (fav !== 0) return fav;
      return (a.label || a.name).localeCompare(b.label || b.name, "ru");
    });
  }, [allAgents, defaultAgent]);

  // Detect "@token" abutting the caret. Requires the @ to start the line or
  // follow whitespace, so emails like foo@bar don't trigger the menu.
  // Inline typing is blocked when a command chip is set (the textarea is for
  // arguments, so a literal "@" should stay literal). The @-button bypasses
  // this via mentionForceOpen — explicit intent to switch agent.
  const mentionToken = useMemo(() => {
    if (!heroMode) return null;
    if (selectedCommand && !mentionForceOpen) return null;
    const before = text.slice(0, caret);
    const m = before.match(/(?:^|\s)@([^\s@]*)$/);
    if (!m) return null;
    const start = caret - (m[1]?.length ?? 0) - 1;
    return { query: m[1] ?? "", start };
  }, [text, caret, heroMode, selectedCommand, mentionForceOpen]);

  useEffect(() => {
    if (!mentionToken) {
      setMentionDismissedAt(null);
      setMentionForceOpen(false);
    }
  }, [mentionToken]);

  const showAgentMenu =
    !!mentionToken && mentionToken.start !== mentionDismissedAt;

  const agentSuggestions = useMemo(() => {
    if (!showAgentMenu) return [];
    const q = mentionToken!.query.toLowerCase();
    if (!q) return sortedAgents;
    return sortedAgents.filter(
      (a) =>
        a.label.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q),
    );
  }, [showAgentMenu, mentionToken, sortedAgents]);

  useEffect(() => { setAgentMenuIndex(0); }, [mentionToken?.query]);

  // Hero "drawer" state — the menu rendered between .hero-input-inner and
  // .hero-input-foot, sharing the card's #fafafa background. Only one of
  // {agents, commands} can be active at a time. We keep the last-shown
  // kind + content in refs so the drawer can animate closed without the
  // content snapping empty mid-collapse.
  // The agents drawer has two sub-modes: a "list" of pickable agents, or an
  // "empty" hint shown when the catalogue holds nothing but the default agent
  // (clicked @-button, or typed @ inline with no agents to offer).
  const agentsListActive = showAgentMenu && agentSuggestions.length > 0;
  const agentsEmptyActive =
    heroMode &&
    (agentHelpOpen || (showAgentMenu && sortedAgents.length === 0));
  const drawerKindNow: "agents" | "commands" | null = heroMode
    ? agentsListActive || agentsEmptyActive
      ? "agents"
      : menuOpen
        ? "commands"
        : null
    : null;
  const drawerOpen = drawerKindNow !== null;
  const lastDrawerKindRef = useRef<"agents" | "commands">("agents");
  const lastAgentSuggestionsRef = useRef<typeof agentSuggestions>([]);
  // Remember the agents sub-mode so the close animation doesn't swap content
  // (e.g. list → empty) mid-collapse.
  const lastAgentsModeRef = useRef<"list" | "empty">("list");
  if (drawerKindNow) lastDrawerKindRef.current = drawerKindNow;
  if (drawerKindNow === "agents")
    lastAgentsModeRef.current = agentsListActive ? "list" : "empty";
  if (agentSuggestions.length > 0)
    lastAgentSuggestionsRef.current = agentSuggestions;
  const renderedDrawerKind = drawerKindNow ?? lastDrawerKindRef.current;
  const renderedAgentsMode =
    drawerKindNow === "agents"
      ? (agentsListActive ? "list" : "empty")
      : lastAgentsModeRef.current;
  const renderedAgentSuggestions =
    showAgentMenu && agentSuggestions.length > 0
      ? agentSuggestions
      : lastAgentSuggestionsRef.current;

  // When the active agent changes (via @-menu pick or pill ×), keep the
  // chipped command only if the new agent surfaces it. Otherwise drop it —
  // the chip is bound to a command_ref the new agent doesn't expose.
  const lastAgentRef = useRef<string | null | undefined>(pendingAgentRefForPill);
  useEffect(() => {
    if (lastAgentRef.current === pendingAgentRefForPill) return;
    lastAgentRef.current = pendingAgentRefForPill;
    setSelectedCommand((prev) => {
      if (!prev) return prev;
      const newAgent = pendingAgentRefForPill
        ? allAgents.find((a) => a.ref === pendingAgentRefForPill)
        : defaultAgent;
      const refs = newAgent?.command_refs ?? [];
      return refs.includes(prev.ref) ? prev : null;
    });
  }, [pendingAgentRefForPill, allAgents, defaultAgent]);

  const pickAgent = (ref: string) => {
    setPendingAgentRef(ref);
    if (!mentionToken) return;
    const before = text.slice(0, mentionToken.start);
    const after = text.slice(caret);
    // Drop the "@query" token; if it was sandwiched by spaces, leave one.
    const glue = before.endsWith(" ") && after.startsWith(" ") ? "" : "";
    const newText = before + glue + after;
    const newPos = before.length;
    setText(newText);
    setCaret(newPos);
    setMentionDismissedAt(null);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(newPos, newPos);
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    });
  };

  useEffect(() => {
    const onInsert = (e: Event) => {
      const id = (e as CustomEvent<{ id: string }>).detail?.id;
      if (!id) return;
      const cmd = commandsById.get(id);
      if (cmd) {
        setSelectedCommand(cmd);
        setText('');
      } else {
        setText(`/${id} `);
      }
      requestAnimationFrame(() => textareaRef.current?.focus());
    };
    window.addEventListener("strands:insert-command", onInsert);
    return () => window.removeEventListener("strands:insert-command", onInsert);
  }, [commandsById]);

  // The @-button (in HeroAgentPill) and any other affordance can ask the
  // composer to toggle the mention menu — symmetric with how the / button
  // toggles the slash menu. If the caret already sits right after an
  // "@token", the click removes it (closes the menu); otherwise it
  // inserts a fresh "@" at the caret. If a chip is up, we DON'T drop it
  // — the chip only goes away if the user actually picks an agent that
  // doesn't have that command (handled by the agent-change effect).
  // mentionForceOpen lets the menu open with a chip up.
  useEffect(() => {
    const onInsertMention = () => {
      const el = textareaRef.current;
      const pos = el?.selectionStart ?? text.length;
      const before = text.slice(0, pos);
      const after = text.slice(pos);

      const m = before.match(/(?:^|\s)@([^\s@]*)$/);
      if (m) {
        // Toggle off: strip the "@token" plus the leading space we
        // auto-inserted (if any), so a round-trip click returns the text
        // exactly to its pre-insert state.
        const tokenLen = (m[1]?.length ?? 0) + 1;
        let start = pos - tokenLen;
        if (start > 0 && before[start - 1] === " ") start -= 1;
        const newText = before.slice(0, start) + after;
        setText(newText);
        setCaret(start);
        setMentionDismissedAt(null);
        setMentionForceOpen(false);
        requestAnimationFrame(() => {
          const t = textareaRef.current;
          if (!t) return;
          t.focus();
          t.setSelectionRange(start, start);
          t.style.height = "auto";
          t.style.height = Math.min(t.scrollHeight, 120) + "px";
        });
        return;
      }

      // Insert
      if (selectedCommand) setMentionForceOpen(true);
      const needsSpace = before.length > 0 && !/\s$/.test(before);
      const insertion = (needsSpace ? " " : "") + "@";
      const newText = before + insertion + after;
      const newPos = before.length + insertion.length;
      setText(newText);
      setCaret(newPos);
      setMentionDismissedAt(null);
      requestAnimationFrame(() => {
        const t = textareaRef.current;
        if (!t) return;
        t.focus();
        t.setSelectionRange(newPos, newPos);
        t.style.height = "auto";
        t.style.height = Math.min(t.scrollHeight, 120) + "px";
      });
    };
    window.addEventListener("strands:insert-mention", onInsertMention);
    return () => window.removeEventListener("strands:insert-mention", onInsertMention);
  }, [text, selectedCommand]);

  // No agents to pick → toggle the drawer's empty state. Doesn't touch the
  // textarea, so dismissing leaves no stray "@" to delete.
  useEffect(() => {
    const onOpenHelp = () => setAgentHelpOpen((v) => !v);
    window.addEventListener("strands:open-agent-help", onOpenHelp);
    return () => window.removeEventListener("strands:open-agent-help", onOpenHelp);
  }, []);

  // The empty state is purely informational — close it as soon as the user
  // does anything else (types, opens the slash menu, picks a command).
  useEffect(() => {
    if (text || menuOpen || selectedCommand) setAgentHelpOpen(false);
  }, [text, menuOpen, selectedCommand]);

  const selectSuggestion = (cmd: Command) => {
    setSelectedCommand(cmd);
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const clearSelectedCommand = () => {
    setSelectedCommand(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  // Mirror handleSend's "is there anything to send?" check so the send
  // button's enabled state matches what Enter actually does. With a command
  // chip up the textarea holds args (often empty) — the payload still comes
  // from the template, so the button must stay enabled.
  const canSend = selectedCommand
    ? expandTemplateWithArgs(selectedCommand.template, text).trim().length > 0
    : text.trim().length > 0;

  const handleSend = () => {
    if (disabled || isStreaming) return;
    let payload: string;
    // When the message comes from a command (chip or typed `/cmd args`),
    // carry its label so the transcript can render a compact chip instead
    // of the whole expanded template.
    let command: { label: string } | undefined;
    if (selectedCommand) {
      payload = expandTemplateWithArgs(selectedCommand.template, text);
      if (!payload.trim()) return;
      command = { label: selectedCommand.label || selectedCommand.id };
    } else {
      const trimmed = text.trim();
      if (!trimmed) return;
      payload = trimmed;
      const parsed = parseCommand(trimmed);
      if (parsed) {
        const cmd = commandsById.get(parsed[0]);
        if (cmd) {
          payload = expandCommand(cmd.template, parsed[1]);
          command = { label: cmd.label || cmd.id };
        }
      }
    }
    onSend(payload, command);
    setText('');
    setSelectedCommand(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (agentHelpOpen && e.key === 'Escape') {
      e.preventDefault();
      setAgentHelpOpen(false);
      return;
    }
    if (showAgentMenu && agentSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setAgentMenuIndex((i) => (i + 1) % agentSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setAgentMenuIndex(
          (i) => (i - 1 + agentSuggestions.length) % agentSuggestions.length,
        );
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        const picked = agentSuggestions[agentMenuIndex];
        if (picked) pickAgent(picked.ref);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionDismissedAt(mentionToken!.start);
        return;
      }
    }
    if (showMenu && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMenuIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMenuIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        const picked = suggestions[menuIndex];
        if (picked) selectSuggestion(picked);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setText('');
        return;
      }
    }
    // Backspace at the very start of an empty textarea pops the chip — same
    // pattern as Slack/Notion @-mentions.
    if (
      selectedCommand &&
      e.key === 'Backspace' &&
      text.length === 0
    ) {
      e.preventDefault();
      clearSelectedCommand();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  };

  // Release the mic if the composer unmounts mid-recording. Without this
  // the red browser indicator (and the underlying track) lingers across
  // route changes — bad UX and a privacy footgun.
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        try { mediaRecorderRef.current.stop(); } catch {}
      }
      mediaStreamRef.current?.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
      mediaRecorderRef.current = null;
    };
  }, []);

  const handleMicToggle = async () => {
    if (recordingState === 'recording') {
      mediaRecorderRef.current?.stop();
      return;
    }
    if (recordingState === 'transcribing') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        if (blob.size === 0) {
          setRecordingState('idle');
          return;
        }
        setRecordingState('transcribing');
        try {
          const transcript = await transcribeAudio(blob);
          if (transcript) {
            setText((prev) => {
              const trimmed = prev.replace(/\s+$/, '');
              return trimmed ? `${trimmed} ${transcript}` : transcript;
            });
            // Auto-grow textarea, move caret to the end, and refocus so the
            // user can immediately keep typing or hit Enter to send.
            requestAnimationFrame(() => {
              const el = textareaRef.current;
              if (!el) return;
              el.focus();
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 120) + 'px';
              const end = el.value.length;
              el.setSelectionRange(end, end);
              setCaret(end);
            });
          }
        } catch (err) {
          console.error('transcription failed', err);
          alert('Не удалось распознать речь. Попробуйте ещё раз.');
        } finally {
          setRecordingState('idle');
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingState('recording');
    } catch (err) {
      console.error('mic access denied', err);
      alert('Не удалось получить доступ к микрофону. Проверьте разрешения браузера.');
      setRecordingState('idle');
    }
  };

  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);

  // Folder button: with the network-folder feature on, open a Local/Network
  // chooser; otherwise pick a local folder directly (legacy behavior).
  const handleFolderClick = async () => {
    if (folderConnected) return; // connected → the × handles disconnect
    if (networkFolderEnabled && onPickNetworkFolder) {
      setSourceMenuOpen((v) => !v);
      return;
    }
    await pickLocalFolder();
  };

  const pickLocalFolder = async () => {
    if (!folderPickerSupported) {
      alert("Подключение папки работает только в Google Chrome (и других браузерах на Chromium).");
      return;
    }
    const isHero = sessionId === null;
    if (isHero) {
      // Hero → just stash the handle and let the user keep composing.
      // We don't create the session here: doing so navigates to
      // /?sid=X, which (a) makes `onPickFolder` race the route change
      // (the "first pick silently fails" bug), and (b) drops the user
      // into "hero with activeId" where the load-history effect clears
      // pendingAgentRef while HeroAgentPill is still on screen — the
      // pill goes blank even though the session has the agent. Mirrors
      // SpaceDashboard's folder flow: ChatPage's pendingFolderHandle
      // effect attaches the handle once the first message creates the
      // session.
      try {
        const handle = await (
          window as unknown as {
            showDirectoryPicker: (opts: { mode: string }) => Promise<FileSystemDirectoryHandle>;
          }
        ).showDirectoryPicker({ mode: "readwrite" });
        setPendingFolderHandle(handle);
        // Warm the hash cache while the user composes. handleSend's
        // uploadFolderToSession consumes this in-memory map so the gated
        // upload only pays network time, not hashing time.
        prehashFolder(handle).catch(() => {});
      } catch (e) {
        const name = (e as { name?: string })?.name;
        if (name !== "AbortError") console.error("Folder pick error:", e);
      }
      return;
    }
    await onPickFolder();
  };

  // "+" button → pick one or more files. Unlike a folder these are a one-shot
  // upload into the session's attachments/ dir (no sync). In hero mode we stash
  // them in the draft store; handleSend uploads them once the first message
  // creates the session. With an active session we upload immediately.
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddFilesClick = () => fileInputRef.current?.click();

  const handleFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // let the user re-pick the same file later
    if (picked.length === 0) return;
    if (sessionId === null) {
      const prev = useAgentDraftStore.getState().pendingFiles ?? [];
      setPendingFiles([...prev, ...picked]);
      return;
    }
    try {
      await uploadAttachmentsToSession(sessionId, picked);
    } catch (err) {
      console.error("attachment upload failed", err);
      alert("Не удалось загрузить файлы. Попробуйте ещё раз.");
    }
  };

  // Transient banner shown when a big paste is diverted to a file (no toast lib
  // in this app). Auto-clears; the appearing file chip is the durable feedback.
  const [pasteNotice, setPasteNotice] = useState<string | null>(null);
  const pasteNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (pasteNoticeTimer.current) clearTimeout(pasteNoticeTimer.current);
  }, []);

  const stageFile = async (file: File) => {
    if (sessionId === null) {
      const prev = useAgentDraftStore.getState().pendingFiles ?? [];
      setPendingFiles([...prev, file]);
      return;
    }
    try {
      await uploadAttachmentsToSession(sessionId, [file]);
    } catch (err) {
      console.error("attachment upload failed", err);
      alert("Не удалось загрузить файл. Попробуйте ещё раз.");
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Only intercept plain-text pastes; let file/image pastes fall through.
    if (e.clipboardData.files && e.clipboardData.files.length > 0) return;
    const pasted = e.clipboardData.getData("text");
    if (!pasted) return;
    if (new Blob([pasted]).size <= PASTE_TO_FILE_BYTES) return;

    e.preventDefault();
    const d = new Date();
    const name =
      `вставка-${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}` +
      `-${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}.txt`;
    const file = new File([pasted], name, { type: "text/plain" });
    void stageFile(file);

    setPasteNotice(`Большой текст добавлен как файл «${name}» — агент прочитает его из вложения.`);
    if (pasteNoticeTimer.current) clearTimeout(pasteNoticeTimer.current);
    pasteNoticeTimer.current = setTimeout(() => setPasteNotice(null), 6000);
  };

  const removePendingFile = (idx: number) => {
    const prev = useAgentDraftStore.getState().pendingFiles ?? [];
    const next = prev.filter((_, i) => i !== idx);
    setPendingFiles(next.length ? next : null);
  };

  const folderLabel = folderConnected && folderName ? folderName : "Выбрать папку";

  return (
    <div className="hero-input-card">
      <div className="hero-input-inner">
        {folderConnected && syncStatus.active && (
          <div className="hero-attached-strip">
            <span className="folder-chip" title="Синхронизация">
              <span className="folder-chip-name">
                {syncStatus.direction === "upload" ? "↑" : "↓"} {syncStatus.current}/{syncStatus.total}
              </span>
            </span>
          </div>
        )}
        {sessionId === null && pendingFiles && pendingFiles.length > 0 && (
          <HeroFileChips files={pendingFiles} onRemove={removePendingFile} />
        )}
        {pasteNotice && (
          <div className="hero-paste-notice" role="status">{pasteNotice}</div>
        )}
        {!heroMode && showAgentMenu && agentSuggestions.length > 0 && (
          <MentionPopover
            open
            items={agentSuggestions.map((a) => ({
              id: a.ref,
              label: a.label || a.name,
              description: a.description || undefined,
            }))}
            currentId={pendingAgentRefForPill}
            activeIndex={agentMenuIndex}
            onPick={pickAgent}
            onHover={setAgentMenuIndex}
            footer={{
              label: "Все агенты",
              onClick: () => {
                setMentionDismissedAt(mentionToken?.start ?? null);
                navigate({ to: "/agents" });
              },
            }}
          />
        )}
        {!heroMode && renderedSuggestions.length > 0 && (
          <div
            className="cmd-suggestions-wrap"
            data-open={menuOpen}
            aria-hidden={!menuOpen}
          >
            <div className="cmd-suggestions" role="listbox">
              {renderedSuggestions.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="option"
                  aria-selected={i === menuIndex}
                  tabIndex={menuOpen ? 0 : -1}
                  className={`cmd-suggestion${i === menuIndex ? ' cmd-suggestion--active' : ''}`}
                  onMouseEnter={() => setMenuIndex(i)}
                  onClick={() => selectSuggestion(s)}
                >
                  <span className="cmd-suggestion-label">{s.label || s.id}</span>
                  {s.description && (
                    <span className="cmd-suggestion-desc">{s.description}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="hero-input-row">
          {selectedCommand && (
            <span className="hero-cmd-chip" title={selectedCommand.description || undefined}>
              <span className="hero-cmd-chip-label">{selectedCommand.label || selectedCommand.id}</span>
              <button
                type="button"
                className="hero-cmd-chip-remove"
                onClick={clearSelectedCommand}
                aria-label="Снять команду"
                title="Снять команду"
              >
                ×
              </button>
            </span>
          )}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => {
              setText(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
            }}
            onSelect={e => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onPaste={handlePaste}
            placeholder={selectedCommand ? "Опишите задачу" : "Чем я могу помочь?"}
            rows={1}
            disabled={disabled && !isStreaming}
            className="hero-input-textarea"
          />
        </div>
        <div className="hero-input-bar">
          <div className="hero-bar-left">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFilesPicked}
            />
            <button
              type="button"
              className="hero-icon-btn"
              onClick={handleAddFilesClick}
              disabled={isStreaming}
              title="Прикрепить файлы"
              aria-label="Прикрепить файлы"
            >
              <svg viewBox="0 0 10.334 10.334" aria-hidden="true" fill="none">
                <path d="M5.16699 0C5.44303 1.44808e-07 5.66682 0.224007 5.66699 0.5V4.66699H9.83398C10.11 4.66717 10.334 4.89096 10.334 5.16699C10.334 5.44303 10.11 5.66682 9.83398 5.66699H5.66699V9.83398C5.66682 10.11 5.44303 10.334 5.16699 10.334C4.89096 10.334 4.66717 10.11 4.66699 9.83398V5.66699H0.5C0.224007 5.66682 1.44809e-07 5.44303 0 5.16699C0 4.89096 0.224007 4.66717 0.5 4.66699H4.66699V0.5C4.66717 0.224007 4.89096 0 5.16699 0Z" fill="currentColor" />
              </svg>
            </button>
            <button
              type="button"
              className="hero-icon-btn hero-icon-btn--slash"
              onClick={() => {
                if (selectedCommand) {
                  // Treat the / button as "switch command" when a chip is up.
                  clearSelectedCommand();
                  setText('/');
                  return;
                }
                setText((t) => (t === '/' ? '' : '/'));
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
              disabled={isStreaming || (commands !== undefined && commands.length === 0)}
              title={commands !== undefined && commands.length === 0 ? "У этого агента нет команд" : "Сценарии"}
              aria-label="Сценарии"
            >
              <svg viewBox="0 0 13 13" aria-hidden="true" fill="none">
                <path d="M11.167 0C12.1793 0.000175703 12.9998 0.820744 13 1.83301V11.167C12.9998 12.1793 12.1793 12.9998 11.167 13H1.83301C0.820744 12.9998 0.000175704 12.1793 0 11.167V1.83301C0.000175913 0.820744 0.820744 0.000175913 1.83301 0H11.167ZM1.83301 1C1.37303 1.00018 1.00018 1.37303 1 1.83301V11.167C1.00018 11.627 1.37303 11.9998 1.83301 12H11.167C11.627 11.9998 11.9998 11.627 12 11.167V1.83301C11.9998 1.37303 11.627 1.00018 11.167 1H1.83301ZM8.14648 4.14648C8.34175 3.95122 8.65825 3.95122 8.85352 4.14648C9.04878 4.34175 9.04878 4.65825 8.85352 4.85352L4.85352 8.85352C4.65825 9.04878 4.34175 9.04878 4.14648 8.85352C3.95122 8.65825 3.95122 8.34175 4.14648 8.14648L8.14648 4.14648Z" fill="currentColor" />
              </svg>
            </button>
            {heroMode && <HeroAgentPill />}
          </div>
          <div className="hero-bar-right">
            <button
              type="button"
              className={`hero-mic-btn${recordingState === 'recording' ? ' hero-mic-btn--recording' : ''}${recordingState === 'transcribing' ? ' hero-mic-btn--busy' : ''}`}
              onClick={handleMicToggle}
              disabled={isStreaming || recordingState === 'transcribing'}
              title={
                recordingState === 'recording'
                  ? 'Остановить запись'
                  : recordingState === 'transcribing'
                  ? 'Распознаём…'
                  : 'Голосовой ввод'
              }
              aria-label="Голосовой ввод"
            >
              {recordingState === 'transcribing' ? (
                <span className="hero-mic-spinner" aria-hidden="true" />
              ) : (
                <svg viewBox="0 0 14 18" aria-hidden="true" fill="none">
                  <path d="M7 11.5C8.65685 11.5 10 10.1569 10 8.5V3.5C10 1.84315 8.65685 0.5 7 0.5C5.34315 0.5 4 1.84315 4 3.5V8.5C4 10.1569 5.34315 11.5 7 11.5Z" fill="currentColor"/>
                  <path d="M13 8.5C13 11.8137 10.3137 14.5 7 14.5C3.68629 14.5 1 11.8137 1 8.5M7 14.5V17.5M4 17.5H10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
            {isStreaming ? (
              <button
                type="button"
                className="hero-stop-btn"
                onClick={onStop}
                title="Остановить"
              />
            ) : (
              <button
                type="button"
                className="hero-send-btn"
                onClick={handleSend}
                disabled={disabled || !canSend || heroSyncing}
                title={heroSyncing ? "Синхронизируем файлы…" : "Отправить"}
              />
            )}
          </div>
        </div>
        {heroMode && (
          <div
            className="hero-input-drawer"
            data-open={drawerOpen ? "true" : "false"}
            aria-hidden={!drawerOpen}
          >
            <div className="hero-input-drawer-content">
              {renderedDrawerKind === "agents" && renderedAgentsMode === "empty" && (
                <div className="hero-input-drawer-empty">
                  <p className="hero-input-drawer-empty-text">
                    Специализированные агенты ещё не настроены — здесь появится
                    их список, когда вы их создадите.
                  </p>
                  <button
                    type="button"
                    className="hero-input-drawer-footer"
                    tabIndex={drawerOpen ? 0 : -1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setAgentHelpOpen(false);
                      navigate({ to: "/agents" });
                    }}
                  >
                    <span>Настроить агентов</span>
                    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" width="14" height="14">
                      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              )}
              {renderedDrawerKind === "agents" && renderedAgentsMode === "list" && (
                <>
                  <div className="hero-input-drawer-list" role="listbox">
                    {renderedAgentSuggestions.map((a, i) => {
                      const isCurrent = a.ref === pendingAgentRefForPill;
                      const label = a.label || a.name;
                      const desc = a.description || "";
                      return (
                        <button
                          key={a.ref}
                          type="button"
                          role="option"
                          aria-selected={i === agentMenuIndex}
                          tabIndex={drawerOpen ? 0 : -1}
                          className={`cmd-suggestion${i === agentMenuIndex ? ' cmd-suggestion--active' : ''}${isCurrent ? ' cmd-suggestion--current' : ''}`}
                          onMouseEnter={() => setAgentMenuIndex(i)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickAgent(a.ref)}
                        >
                          <span className="cmd-suggestion-current-marker" aria-hidden="true">·</span>
                          <span className="cmd-suggestion-label">{label}</span>
                          {desc && (
                            <span className="cmd-suggestion-desc">{desc}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="hero-input-drawer-footer"
                    tabIndex={drawerOpen ? 0 : -1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setMentionDismissedAt(mentionToken?.start ?? null);
                      navigate({ to: "/agents" });
                    }}
                  >
                    <span>Все агенты</span>
                    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" width="14" height="14">
                      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </>
              )}
              {renderedDrawerKind === "commands" && (
                <div className="hero-input-drawer-list" role="listbox">
                  {renderedSuggestions.map((s, i) => (
                    <button
                      key={s.id}
                      type="button"
                      role="option"
                      aria-selected={i === menuIndex}
                      tabIndex={drawerOpen ? 0 : -1}
                      className={`cmd-suggestion${i === menuIndex ? ' cmd-suggestion--active' : ''}`}
                      onMouseEnter={() => setMenuIndex(i)}
                      onClick={() => selectSuggestion(s)}
                    >
                      <span className="cmd-suggestion-label">{s.label || s.id}</span>
                      {s.description && (
                        <span className="cmd-suggestion-desc">{s.description}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {!hideFooter && (
      <div className="hero-input-foot">
        <div className="hero-foot-folder-wrap">
          <button
            type="button"
            className="hero-foot-folder"
            onClick={handleFolderClick}
            disabled={isStreaming}
            title={folderConnected ? folderName ?? "" : "Выбрать папку"}
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" fill="none">
              <path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h3.586a1.5 1.5 0 0 1 1.06.44l1.415 1.414a1.5 1.5 0 0 0 1.06.439H16.5A1.5 1.5 0 0 1 18 7.793V14.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 2 14.5v-9Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
            <span className="hero-foot-folder-label">{folderLabel}</span>
            {folderConnected && (
              <span
                role="button"
                tabIndex={0}
                className="hero-foot-folder-remove"
                onClick={(e) => { e.stopPropagation(); onDisconnectFolder(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onDisconnectFolder(); } }}
                title="Отключить папку"
                aria-label="Отключить папку"
              >
                ×
              </span>
            )}
          </button>
          {sourceMenuOpen && (
            <>
              <div className="folder-source-backdrop" onClick={() => setSourceMenuOpen(false)} />
              <div className="folder-source-menu" role="menu">
                <button
                  type="button"
                  className="folder-source-item"
                  role="menuitem"
                  onClick={() => { setSourceMenuOpen(false); void pickLocalFolder(); }}
                >
                  <span className="folder-source-item-title">Локальная папка</span>
                  <span className="folder-source-item-hint">С этого устройства</span>
                </button>
                <button
                  type="button"
                  className="folder-source-item"
                  role="menuitem"
                  onClick={() => { setSourceMenuOpen(false); onPickNetworkFolder?.(); }}
                >
                  <span className="folder-source-item-title">Сетевая папка</span>
                  <span className="folder-source-item-hint">Общий доступ по сети</span>
                </button>
              </div>
            </>
          )}
        </div>
        <HeroConnectorsPill sessionId={sessionId} pendingAgentRef={pendingAgentRefForPill} />
      </div>
      )}
    </div>
  );
}
